import { OperatorElearningTopicsService } from '../../../../../api/api-services/operator-elearning-topics.service';
import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { MOBILE_LAYOUT_QUERY } from '../../../../shared/services/mobile-shell.service';
import { IFasTableDropdownFilter, IFasTableFilters } from 'fas-services';
import { Component, OnInit, inject } from '@angular/core';

@Component({
  selector: 'app-topic-list',
  templateUrl: './topic-list.component.html',
  styleUrl: './topic-list.component.scss'
})
export class TopicListComponent implements OnInit {

  private _operatorElearningTopicsService = inject(OperatorElearningTopicsService);
  private _operatorElearningTopicCategoriesService = inject(OperatorElearningTopicCategoriesService);

  public originalTopicsList: IOperatorElearningTopicVm[] = [];
  public topicsList: IOperatorElearningTopicVm[] = [];
  public selectedTopic!: IOperatorElearningTopicVm;
  public isLoading = false;
  public currentRows = 10;
  public currentFirst = 0;
  public topicIndex = 0;

  public dropdownFilters: IFasTableDropdownFilter[] = [];
  private _searchValue = '';
  private _selectedCategoryIds: number[] = [];

  public detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  public topicColumns = [
    {
      field: 'order',
      header: 'Kolejność',
      contentClass: 'text-sm',
      sortable:true,
    },
    {
      field: 'name',
      header: 'Nazwa rozdziału',
      contentClass: 'text-sm',
      columnClass: 'name-col',
      sortable:true,
    },
    {
      field: 'elearningTopicCategoryName',
      header: 'Przypisany dział',
      contentClass: 'text-sm',
      sortable:true,
    },
    {
      field: 'translationsInfo',
      header: 'Wersje językowe',
      contentClass: 'text-sm',
      valueFunction: this.getTranslationsInfo.bind(this),
    },
    {
      field: 'created',
      header: 'Data utworzenia',
      contentClass: 'text-sm',
      dateField: true
    },
    {
      header: 'Akcje',
      contentClass: 'text-sm',
      actions: [
        {
          buttonClass: 'pi pi-eye cursor-pointer text-center appear transparent-button',
          callback: this.editTopic.bind(this),
          tooltip: 'Podgląd',
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.loadPaginationState();
    this.loadAvailableCategories();
    this.loadTopics();
  }

  private loadAvailableCategories(): void {
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories) => {
        this.dropdownFilters = [
          {
            id: 'categories',
            placeholder: 'Filtruj po dziale',
            options: categories.map((c) => ({ label: c.name, value: c.id })),
          },
        ];
      },
      error: (error) => {
        this.dropdownFilters = [];
      },
    });
  }

  private loadTopics(): void {
    this.isLoading = true;
    this._operatorElearningTopicsService
      .get(undefined, this._searchValue || undefined, this._selectedCategoryIds.length ? this._selectedCategoryIds : undefined)
      .subscribe({
        next: (topics) => {
          this.originalTopicsList = topics;
          this.topicsList = (topics);
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        }
      });
  }

  public onFiltersChange(filters: IFasTableFilters): void {
    this._searchValue = filters.searchString ?? '';
    this._selectedCategoryIds = filters.dropdownValues?.['categories'] ?? [];
    this.loadTopics();
  }

  public getTranslationsInfo(topic: IOperatorElearningTopicVm): string {
    if (topic.elearningTopicTranslation && topic.elearningTopicTranslation.length > 0) {
      const nonPolishTranslations = topic.elearningTopicTranslation.filter((t: any) => t.languageCode !== 'pl' && t.nameOverride);

      if (nonPolishTranslations.length > 0) {
        const languages = nonPolishTranslations.map((t: any) => t.languageCode?.toUpperCase()).join(', ');
        return `${languages}`;
      } else {
        return 'Brak tłumaczeń';
      }
    }
    return 'Brak tłumaczeń';
  }

  /** On a phone the preview button is in the last column, off the screen: a tap on the row opens the topic. */
  public onRowTap = (topic: any, index: number): void => {
    if (window.matchMedia(MOBILE_LAYOUT_QUERY).matches) {
      this.editTopic(topic, index);
    }
  };

  public editTopic(topic: any, index: number): void {
    const originalTopic = this.originalTopicsList.find(t => t.id === topic.id);
    if (originalTopic) {
      this.selectedTopic = originalTopic;
      this.topicIndex = this.originalTopicsList.findIndex(t => t.id === topic.id);
      this.detailsControl.isOpen = true;
      this.detailsControl.isEditing = true;
    }
  }

  public closeDetails(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
  }

  public refreshTopics(): void {
    this.loadAvailableCategories();
    this.loadTopics();
  }

  public setCurrentPosition(event: any): void {
    this.currentFirst = event.first;
    this.currentRows = event.rows;
    this.savePaginationState();
  }

  private loadPaginationState(): void {
    const savedState = localStorage.getItem('elearning-topic-pagination');
    if (savedState) {
      const state = JSON.parse(savedState);
      this.currentFirst = state.currentFirst || 0;
      this.currentRows = state.currentRows || 10;
    }
  }

  private savePaginationState(): void {
    const state = {
      currentFirst: this.currentFirst,
      currentRows: this.currentRows
    };
    localStorage.setItem('elearning-topic-pagination', JSON.stringify(state));
  }
}
